import { useEffect, useState } from 'react';
import { CustomMetaTitle } from 'Components/CustomMetaTitle';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  ISubject,
  IRecentRequests,
  IRequestCategories,
  ISubCategory,
  getPopularCategories,
  getRecentRequests,
  getRequestCategories,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { ContentHeader } from 'Components/ContentHeader';
import { ParentCategoriesHeaderContent } from './ParentCategoriesHeaderContent';
import { ParentCategoriesList } from './ParentCategoriesList';
import { SubCategoriesList } from './SubCategoriesList';
import { SubCategoriesHeaderContent } from './SubCategoriesHeaderContent';
import { UploadFiles } from './UploadFiles';
import { SuccessHeaderContent } from './SuccessHeaderContent';
import { useAppSelector, useIsOtpLogin } from 'Hooks';
import { useParams } from 'react-router-dom';

export enum TAB_VALUE_TYPE {
  SUBJECTS,
  DEPARTMENT,
}

export const UniversalRequest = () => {
  const { isOtp, isAuth } = useIsOtpLogin();

  const [tabValue, setTabValue] = useState<TAB_VALUE_TYPE>(
    TAB_VALUE_TYPE.SUBJECTS
  );
  const [specialCategoriesData, setSpecialCategoriesData] = useState<{
    data: IRecentRequests[];
    type: string;
  }>({
    data: [],
    type: '',
  });
  const [categoriesList, setCategoriesList] = useState<{
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  }>({
    subjects: [],
    departments: [],
    subSubjects: [],
  });
  const [isSuccessPage, setIsSuccessPage] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const dictionary = Dictionary[DICTIONARY.UniversalRequest];

  const handleTabChange = (_: any, newValue: TAB_VALUE_TYPE) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isOtp || !isAuth) {
        const popularCategories = await getPopularCategories();
        if (popularCategories)
          setSpecialCategoriesData({
            data: popularCategories,
            type: 'popular',
          });
      } else {
        const recentRequests = await getRecentRequests();
        if (recentRequests && recentRequests.length > 0) {
          setSpecialCategoriesData({
            data: recentRequests,
            type: 'recent',
          });
        } else {
          const popularCategories = await getPopularCategories();
          if (popularCategories)
            setSpecialCategoriesData({
              data: popularCategories,
              type: 'popular',
            });
        }
      }
    };

    fetchData();
  }, [imposterId, isOtp, isAuth]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const categories = await getRequestCategories();

      if (categories) {
        const sortedCategories: IRequestCategories = categories;

        setCategoriesList(sortedCategories);
        setIsLoading(false);
      }
    };
    getData();
  }, [imposterId, isAuth, isOtp, dictionary]);

  const resetRequest = () => {
    setIsSuccessPage(false);
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
  };

  const handleCategoryTitleClick = (categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null); // Reset the selected subcategory when a category is clicked
  };

  const handleSubCategoryClick = (subSubjectId: number | null) => {
    if (!isOtp) {
      const subCategory = categoriesList.subSubjects.find(
        (subSubject) => subSubject.id === subSubjectId
      );
      if (subCategory?.link) window.location.assign(subCategory.link);
    } else {
      setSelectedSubCategoryId(subSubjectId);
      return;
    }

    setSelectedSubCategoryId(subSubjectId);
  };

  const getParentTitleById = (categoryId: number): string => {
    if (tabValue === TAB_VALUE_TYPE.SUBJECTS) {
      return (
        categoriesList.subjects.find((category) => category.id === categoryId)
          ?.title || ''
      );
    } else {
      return (
        categoriesList.departments.find(
          (category) => category.id === categoryId
        )?.title || ''
      );
    }
  };

  const getSubtitleById = (categoryId: number) => {
    const subSubject = categoriesList.subSubjects.find(
      (subSubject) => subSubject.id === categoryId
    );
    return subSubject;
  };

  const handleSuccess = () => {
    setIsSuccessPage(true);
  };

  const getChosenSubjectTitle = () => {
    const parentLabel =
      selectedCategoryId && getParentTitleById(selectedCategoryId);
    const subLabel =
      selectedSubCategoryId && getSubtitleById(selectedSubCategoryId);

    return parentLabel && subLabel && subLabel.title
      ? `${parentLabel} > ${subLabel.title}`
      : (subLabel && subLabel.title) || '';
  };

  const params = useParams();

  useEffect(() => {
    if (!!params.tabId) setTabValue(parseInt(params.tabId) - 1);

    if (!!params.categoryId) setSelectedCategoryId(parseInt(params.categoryId));
  }, [params.categoryId, params.tabId]);

  const clearCategory = (isReset?: boolean) => {
    if (params.categoryId)
      window.location.assign(
        `${process.env.REACT_APP_REDIRECT_URL}universalRequest`
      );
    else setSelectedCategoryId(null);
    if (isReset) setTabValue(TAB_VALUE_TYPE.SUBJECTS);
  };
  const clearSubCategory = () => {
    if (params.subCategoryId)
      window.location.assign(
        `${process.env.REACT_APP_REDIRECT_URL}universalRequest/${params.tabId}/${params.categoryId}`
      );
    else setSelectedSubCategoryId(null);
  };

  const getSubSubject = () =>
    categoriesList.subSubjects.find(
      (subSubject) => subSubject.id === selectedSubCategoryId
    );

  return (
    <>
      <CustomMetaTitle
        title={`${dictionary.title} - ${dictionary.metaTitleStepOne}`}
        disablePrefix={isOtp || !isAuth ? true : false}
      />
      <ContentHeader
        isSuccessPage={isSuccessPage}
        isOtp={isOtp}
        isAuth={isAuth}
        selectedCategoryId={selectedCategoryId}
        selectedSubCategoryId={selectedSubCategoryId}
        clearCategory={clearCategory}
        clearSubCategory={clearSubCategory}
      >
        {/* render parent categories header content */}
        {selectedCategoryId === null && selectedSubCategoryId === null && (
          <ParentCategoriesHeaderContent
            handleTabChange={handleTabChange}
            selectedCategoryId={selectedCategoryId}
            tabValue={tabValue}
          />
        )}
        {/* render sub categories header content */}
        {selectedCategoryId !== null && selectedSubCategoryId === null && (
          <SubCategoriesHeaderContent
            getParentTitleById={getParentTitleById(selectedCategoryId)}
            parentId={selectedCategoryId}
            tabValue={tabValue}
          />
        )}
        {/* render upload files header content */}
        {selectedSubCategoryId !== null && !isSuccessPage && (
          <>
            <SubCategoriesHeaderContent
              getParentTitleById={
                categoriesList.subSubjects.find(
                  (subSubject) => subSubject.id === selectedSubCategoryId
                )?.parentTitle ||
                (selectedCategoryId
                  ? getParentTitleById(selectedCategoryId)
                  : '')
              }
              getSubtitleObj={getSubtitleById(selectedSubCategoryId)}
              parentId={selectedCategoryId!}
              tabValue={tabValue}
            />
          </>
        )}
        {isSuccessPage && <SuccessHeaderContent tabValue={tabValue} />}
      </ContentHeader>

      <div style={{ marginBottom: '120px' }}>
        {/* render parent categories list */}
        {selectedCategoryId === null && selectedSubCategoryId === null && (
          <ParentCategoriesList
            categoriesList={categoriesList}
            specialCategoriesData={specialCategoriesData}
            tabValue={tabValue}
            handleCategoryClick={handleCategoryTitleClick}
            handleSubCategoryClick={handleSubCategoryClick}
            handleTabChange={handleTabChange}
          />
        )}
        {/* render sub categories list */}
        {selectedCategoryId !== null && selectedSubCategoryId === null && (
          <SubCategoriesList
            goBack={clearCategory}
            selectedCategoryId={selectedCategoryId}
            categoriesList={categoriesList}
            tabValue={tabValue}
            handleSubCategoryClick={handleSubCategoryClick}
            getParentTitleById={getParentTitleById(selectedCategoryId)}
            isLoading={isLoading}
          />
        )}
        {/* render upload files */}
        {selectedSubCategoryId !== null && (
          <UploadFiles
            parentId={selectedCategoryId}
            subId={selectedSubCategoryId}
            clearSubCategory={clearSubCategory}
            handleSuccess={handleSuccess}
            resetRequest={resetRequest}
            titleLabel={getChosenSubjectTitle()}
            onlineFormLink={getSubSubject()?.onlineFormLink}
            onlineFormLinkPrefixText={getSubSubject()?.onlineFormLinkPrefixText}
            onlineFormLinkText={getSubSubject()?.onlineFormLinkText}
            isDownload={getSubSubject()?.isOnlineFormLinkForDownload}
            isOtp={isOtp}
            subCategory={getSubSubject()}
          />
        )}
      </div>
    </>
  );
};
